import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { VideoThumbnail } from '..';
import MastheadVideoThumbnail from '../MastheadVideoThumbnail';
import { VideoContent } from '../../../types/content';

interface VideoCarouselProps {
  videos: VideoContent[];
  className?: string;
  autoScrollInterval?: number;
  forceLeftToRight?: boolean;
  useMastheadStyle?: boolean;
}

const VideoCarousel: React.FC<VideoCarouselProps> = ({
  videos,
  className = '',
  autoScrollInterval = 4000,
  useMastheadStyle = false,
}) => {
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Number of items to show at once
  const itemsPerView = useMastheadStyle ? 2 : 3;
  
  // Create one extra set of items for smooth transition
  const extendedVideos = [...videos, ...videos.slice(0, itemsPerView)];

  // Handle the transition end
  const handleTransitionEnd = () => {
    if (currentIndex >= videos.length) {
      setIsTransitioning(false);
      setCurrentIndex(currentIndex % videos.length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
    }
  };

  // Auto-scroll effect
  useEffect(() => {
    if (isPaused || !videos.length) return;

    const interval = setInterval(() => {
      setCurrentIndex(current => current + 1);
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [isPaused, videos.length, autoScrollInterval]);

  // Initialize transition state
  useEffect(() => {
    setIsTransitioning(true);
  }, []);

  if (!videos.length) return null;

  // Calculate transform based on current index
  const calculateTransform = () => {
    const container = containerRef.current;
    if (!container) return 'translateX(0)';
    
    const itemContainer = container.querySelector('[data-item-container]');
    const itemWidth = itemContainer?.clientWidth || 0;
    const gapWidth = useMastheadStyle ? 16 : 4;
    const transform = currentIndex * (itemWidth + gapWidth) * (isRTL ? 1 : -1);
    return `translateX(${transform}px)`;
  };

  const ThumbnailComponent = useMastheadStyle ? MastheadVideoThumbnail : VideoThumbnail;

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Carousel Container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden"
      >
        <div
          className={`
            flex
            ${useMastheadStyle ? 'gap-4' : 'gap-1'}
          `}
          style={{
            transform: calculateTransform(),
            transition: isTransitioning ? 'transform 500ms ease-out' : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {extendedVideos.map((video, index) => (
            <div
              key={`${video.id}-${index}`}
              data-item-container
              style={{
                flexBasis: useMastheadStyle ? 'calc(50% - 8px)' : 'calc(33.333% - 3px)',
                flexShrink: 0,
                flexGrow: 0,
              }}
              className={`
                ${!useMastheadStyle && 'rounded-xl p-4 bg-surface-primary/10'}
              `}
            >
              <ThumbnailComponent
                {...video}
                className="w-full h-full"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation dots */}
      <div className="flex justify-center items-center mt-6 gap-2">
        {videos.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`
              h-2 transition-all duration-300
              rounded-full
              ${currentIndex % videos.length === index
                ? `w-6 ${theme.mode === 'dark' 
                    ? 'bg-primary-400' 
                    : 'bg-background-primary opacity-60'}`
                : `w-2 ${theme.mode === 'dark' 
                    ? 'bg-primary-800' 
                    : 'bg-background-primary opacity-10'}`
              }
              hover:scale-105
              transform
            `}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel; 